import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Withdrawal, WithdrawalFilters, GetWithdrawalsResponse } from '../../types';
import apiService from '../../services/api';

interface WithdrawalsState {
  withdrawals: Withdrawal[];
  selectedWithdrawal: Withdrawal | null;
  statistics: {
    totalAmount: number;
    averageAmount: number;
    totalCount: number;
    statusBreakdown: {
      pending: {
        count: number;
        totalAmount: number;
      };
      approved: {
        count: number;
        totalAmount: number;
      };
      rejected: {
        count: number;
        totalAmount: number;
      };
      cancelled: {
        count: number;
        totalAmount: number;
      };
    };
  };
  filters: WithdrawalFilters;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  loading: boolean;
  error: string | null;
}

const initialState: WithdrawalsState = {
  withdrawals: [],
  selectedWithdrawal: null,
  statistics: {
    totalAmount: 0,
    averageAmount: 0,
    totalCount: 0,
    statusBreakdown: {
      pending: {
        count: 0,
        totalAmount: 0,
      },
      approved: {
        count: 0,
        totalAmount: 0,
      },
      rejected: {
        count: 0,
        totalAmount: 0,
      },
      cancelled: {
        count: 0,
        totalAmount: 0,
      },
    },
  },
  filters: {},
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  },
  loading: false,
  error: null,
};

export const fetchWithdrawals = createAsyncThunk(
  'withdrawals/fetchWithdrawals',
  async ({ page, limit, filters }: { page: number; limit: number; filters?: WithdrawalFilters }) => {
    const response = await apiService.getWithdrawals(page, limit, filters);
    return response;
  }
);

export const fetchWithdrawal = createAsyncThunk(
  'withdrawals/fetchWithdrawal',
  async (withdrawalId: string) => {
    const response = await apiService.getWithdrawal(withdrawalId);
    return response;
  }
);

export const updateWithdrawalStatus = createAsyncThunk(
  'withdrawals/updateWithdrawalStatus',
  async ({ withdrawalId, status, adminNote }: { withdrawalId: string; status: string; adminNote?: string }) => {
    const response = await apiService.updateWithdrawalStatus(withdrawalId, status, adminNote);
    return { ...response, withdrawalId };
  }
);

const withdrawalsSlice = createSlice({
  name: 'withdrawals',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedWithdrawal: (state, action: PayloadAction<Withdrawal | null>) => {
      state.selectedWithdrawal = action.payload;
    },
    setFilters: (state, action: PayloadAction<WithdrawalFilters>) => {
      state.filters = action.payload;
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.pagination.page = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWithdrawals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWithdrawals.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.withdrawals = action.payload.data;
          state.pagination = {
            page: action.payload.pagination.page,
            limit: action.payload.pagination.limit,
            total: action.payload.pagination.total,
            pages: action.payload.pagination.pages,
          };
          state.statistics = action.payload.statistics || {
            totalAmount: 0,
            averageAmount: 0,
            totalCount: 0,
            statusBreakdown: {
              pending: { count: 0, totalAmount: 0 },
              approved: { count: 0, totalAmount: 0 },
              rejected: { count: 0, totalAmount: 0 },
              cancelled: { count: 0, totalAmount: 0 },
            },
          };
        }
      })
      .addCase(fetchWithdrawals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch withdrawals';
      })
      .addCase(fetchWithdrawal.fulfilled, (state, action) => {
        state.selectedWithdrawal = action.payload;
      })
      .addCase(updateWithdrawalStatus.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.withdrawals.findIndex(w => w.id === action.payload.withdrawalId);
          if (index !== -1) {
            state.withdrawals[index] = action.payload.data;
          }
        }
      });
  },
});

export const { clearError, setSelectedWithdrawal, setFilters, clearFilters, setPage } = withdrawalsSlice.actions;
export default withdrawalsSlice.reducer; 